/**
 * Camera shake (trauma model with smooth sum-of-sines noise, no random jitter) and hit-stop
 * (a brief slow-down of animation and particle time). Pure logic; the renderer applies the
 * offset to the camera for one render and restores it, so picking is unaffected.
 */

export class CameraShake {
  /** 0..1; displacement scales with trauma². */
  trauma = 0;
  enabled = true;
  intensity = 1;
  /** Max displacement in yards at trauma 1. */
  maxOffset = 0.32;
  private t = 0;

  add(amount: number): void {
    if (!this.enabled || this.intensity <= 0) return;
    this.trauma = Math.min(1, this.trauma + amount * Math.min(1.25, this.intensity));
  }

  update(dtS: number): void {
    this.t += dtS;
    this.trauma = Math.max(0, this.trauma - dtS * 2.2);
  }

  /** Current offset along camera right/up (yards), written into `out`. */
  offset(out: { x: number; y: number }): { x: number; y: number } {
    if (!this.enabled || this.trauma <= 0) {
      out.x = out.y = 0;
      return out;
    }
    const s = this.trauma * this.trauma * this.maxOffset;
    const t = this.t * 38;
    out.x = s * (Math.sin(t * 1.0) * 0.6 + Math.sin(t * 2.31 + 1.7) * 0.4);
    out.y = s * (Math.sin(t * 1.17 + 0.5) * 0.6 + Math.sin(t * 2.73 + 2.9) * 0.4);
    return out;
  }
}

export class HitStop {
  /** Seconds of real time left in the current stop. */
  private left = 0;
  private cooldown = 0;
  /** Real time since the current stop started, and its planned length (s). */
  private sinceStart = Infinity;
  private cur = 0;
  intensity = 1;

  /**
   * Trigger a stop of `ms` (scaled by intensity). Rate limited so streams of crits don't stutter,
   * but a bigger stop landing within MERGE_S of the current one upgrades it (a clip's contact
   * freeze and the crit / killing blow that lands on the same frame become one longer beat).
   * Returns true when a stop started or grew.
   */
  trigger(ms: number): boolean {
    if (this.intensity <= 0.05 || ms <= 0) return false;
    const want = (ms / 1000) * Math.min(1.2, this.intensity);
    if (this.sinceStart < HitStop.MERGE_S) {
      const extra = want - this.cur;
      if (extra <= 0.004) return false;
      this.left += extra;
      this.cur = want;
      this.cooldown = Math.max(this.cooldown, HitStop.COOLDOWN_S + want);
      return true;
    }
    if (this.cooldown > 0) return false;
    this.left = Math.max(this.left, want);
    this.cur = want;
    this.sinceStart = 0;
    this.cooldown = HitStop.COOLDOWN_S + want;
    return true;
  }

  /** Advance by real time; returns the time scale for this frame (≈0.1 during a stop). */
  update(dtS: number): number {
    this.sinceStart += dtS;
    this.cooldown = Math.max(0, this.cooldown - dtS);
    if (this.left <= 0) return 1;
    this.left -= dtS;
    return 0.1;
  }

  get active(): boolean {
    return this.left > 0;
  }

  static readonly MERGE_S = 0.09;
  static readonly COOLDOWN_S = 0.5;
}

/**
 * Directional camera kick and FOV punch: a slightly under-damped spring in screen space (yards
 * along camera right / up) plus an FOV offset in degrees (positive = zoom in). A heavy hit pushes
 * the view along the blow and snaps back; a crit or finisher also tightens the FOV for a beat.
 * Pure logic; the renderer offsets the camera for one render and restores it.
 */
export class CameraKick {
  enabled = true;
  intensity = 1;
  x = 0;
  y = 0;
  fov = 0;
  private vx = 0;
  private vy = 0;
  private vf = 0;
  /** Max displacement (yd) and FOV punch (deg). */
  static readonly MAX_OFFSET = 0.32;
  static readonly MAX_FOV = 2.4;
  private static readonly K = 240;
  private static readonly C = 2 * Math.sqrt(240) * 0.62;

  /** Kick along screen direction (dx, dy) (need not be normalized) by `mag` yards; FOV punch `fovDeg`. */
  punch(dx: number, dy: number, mag: number, fovDeg = 0): void {
    if (!this.enabled || this.intensity <= 0) return;
    const k = Math.min(1.2, this.intensity);
    const l = Math.hypot(dx, dy);
    if (l > 1e-6 && mag > 0) {
      const v = (mag * k * 26) / l;
      this.vx += dx * v;
      this.vy += dy * v;
    }
    if (fovDeg > 0) this.vf += fovDeg * k * 26;
  }

  update(dtS: number): void {
    // Sub-step so a long frame can't blow the spring up.
    let left = Math.min(0.1, Math.max(0, dtS));
    while (left > 1e-6) {
      const h = Math.min(left, 1 / 120);
      left -= h;
      this.vx += (-CameraKick.K * this.x - CameraKick.C * this.vx) * h;
      this.vy += (-CameraKick.K * this.y - CameraKick.C * this.vy) * h;
      this.vf += (-CameraKick.K * this.fov - CameraKick.C * this.vf) * h;
      this.x += this.vx * h;
      this.y += this.vy * h;
      this.fov += this.vf * h;
    }
    const m = CameraKick.MAX_OFFSET;
    this.x = Math.max(-m, Math.min(m, this.x));
    this.y = Math.max(-m, Math.min(m, this.y));
    this.fov = Math.max(-CameraKick.MAX_FOV * 0.3, Math.min(CameraKick.MAX_FOV, this.fov));
    if (Math.abs(this.x) + Math.abs(this.y) + Math.abs(this.vx) + Math.abs(this.vy) < 1e-4) this.x = this.y = this.vx = this.vy = 0;
    if (Math.abs(this.fov) + Math.abs(this.vf) < 1e-4) this.fov = this.vf = 0;
  }

  get active(): boolean {
    return this.x !== 0 || this.y !== 0 || this.fov !== 0;
  }

  reset(): void {
    this.x = this.y = this.fov = this.vx = this.vy = this.vf = 0;
  }
}
